import { useState, useEffect } from 'react';
import {
    Activity,
    AlertTriangle,
    Shield,
    Play,
    Pause,
    RefreshCw,
    Clock,
    CheckCircle,
    AlertOctagon,
    ArrowUpRight
} from 'lucide-react';

interface SecurityAlert {
    id: string;
    source: string;
    severity: 'low' | 'medium' | 'high' | 'critical';
    title: string;
    description: string;
    timestamp: string;
}

interface Investigation {
    alertId: string;
    status: 'investigating' | 'completed' | 'escalated' | 'auto_remediated';
    summary: string;
    startTime: string;
    endTime?: string;
}

interface AlertWithInvestigation {
    alert: SecurityAlert;
    investigation: Investigation;
}

interface MonitoringStatus {
    isRunning: boolean;
    alertsProcessed: number;
    alertsInQueue: number;
    activeInvestigations: number;
}

const API_BASE = 'http://localhost:8080';

export function MonitoringPanel() {
    const [status, setStatus] = useState<MonitoringStatus | null>(null);
    const [alerts, setAlerts] = useState<AlertWithInvestigation[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Simulation state for fallback
    const [isSimulating, setIsSimulating] = useState(false);

    useEffect(() => {
        let simulationInterval: any;

        if (isSimulating) {
            // Generate fake alerts if simulating
            simulationInterval = setInterval(() => {
                const newAlert: AlertWithInvestigation = {
                    alert: {
                        id: `SIM-${Date.now()}`,
                        source: ['CrowdStrike', 'Splunk', 'Okta', 'Sentinel'][Math.floor(Math.random() * 4)],
                        severity: ['low', 'medium', 'high', 'critical'][Math.floor(Math.random() * 4)] as any,
                        title: 'Simulated Security Alert',
                        description: 'This is a simulated alert because the API server is unreachable.',
                        timestamp: new Date().toISOString()
                    },
                    investigation: {
                        alertId: `SIM-${Date.now()}`,
                        status: 'completed',
                        summary: 'Auto-investigated by local agent simulation.',
                        startTime: new Date().toISOString(),
                        endTime: new Date().toISOString()
                    }
                };
                setAlerts(prev => [newAlert, ...prev].slice(0, 50));
                setStatus(prev => prev ? ({ ...prev, alertsProcessed: (prev.alertsProcessed || 0) + 1, alertsInQueue: 0 }) : null);
            }, 5000);
        }

        return () => clearInterval(simulationInterval);
    }, [isSimulating]);

    const fetchStatus = async () => {
        if (isSimulating) return; // Don't fetch if simulating
        try {
            const res = await fetch(`${API_BASE}/api/monitoring/status`);
            if (res.ok) {
                const data = await res.json();
                setStatus(data);
                setError(null);
            } else {
                throw new Error(`Status check failed: ${res.status}`);
            }
        } catch (e) {
            console.error('Failed to fetch status:', e);
            if (!isSimulating && !status) {
                setError('Backend unreachable. Switched to Simulation Mode.');
                setIsSimulating(true);
                setStatus({
                    isRunning: true,
                    alertsProcessed: 0,
                    alertsInQueue: 0,
                    activeInvestigations: 0
                });
            }
        }
    };

    const fetchAlerts = async () => {
        if (isSimulating) return;
        if (!status && !isSimulating) return; // Don't fetch alerts if status check failed and not simulating
        try {
            const res = await fetch(`${API_BASE}/api/monitoring/alerts`);
            if (res.ok) {
                const data = await res.json();
                setAlerts(data.alerts || []);
            }
        } catch (e) {
            console.error('Failed to fetch alerts:', e);
        }
    };

    const toggleMonitoring = async () => {
        if (isSimulating) {
            setIsSimulating(false);
            setStatus(null);
            setAlerts([]);
            setError(null); // Clear error when exiting simulation
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const endpoint = status?.isRunning ? 'stop' : 'start';
            const res = await fetch(`${API_BASE}/api/monitoring/${endpoint}`, { method: 'POST' });
            if (res.ok) {
                const data = await res.json();
                setStatus(data.status);
            } else {
                setError('Failed to toggle monitoring');
            }
        } catch (e) {
            setError('Connection error. Switching to Simulation Mode.');
            setIsSimulating(true);
            setStatus({
                isRunning: true,
                alertsProcessed: 0,
                alertsInQueue: 0,
                activeInvestigations: 0
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStatus();
        fetchAlerts();

        // Poll every 5 seconds
        const interval = setInterval(() => {
            fetchStatus();
            fetchAlerts();
        }, 5000);

        return () => clearInterval(interval);
    }, []);

    const getSeverityColor = (severity: string) => {
        switch (severity) {
            case 'critical': return 'text-red-400 bg-red-500/20 border-red-500/30';
            case 'high': return 'text-orange-400 bg-orange-500/20 border-orange-500/30';
            case 'medium': return 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30';
            case 'low': return 'text-green-400 bg-green-500/20 border-green-500/30';
            default: return 'text-slate-400 bg-slate-500/20 border-slate-500/30';
        }
    };

    const getStatusIcon = (invStatus: string) => {
        switch (invStatus) {
            case 'completed': return <CheckCircle className="w-4 h-4 text-green-400" />;
            case 'escalated': return <AlertOctagon className="w-4 h-4 text-orange-400" />;
            case 'investigating': return <RefreshCw className="w-4 h-4 text-blue-400 animate-spin" />;
            case 'auto_remediated': return <Shield className="w-4 h-4 text-primary-400" />;
            default: return <Clock className="w-4 h-4 text-slate-400" />;
        }
    };

    return (
        <div className="space-y-6">
            {/* Header with Controls */}
            <div className="glass rounded-2xl p-6">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center shadow-lg shadow-primary-500/25">
                            <Activity className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-white">Autonomous Monitoring</h2>
                            <p className="text-sm text-slate-400">Real-time threat detection & response</p>
                        </div>
                    </div>
                    <button
                        onClick={toggleMonitoring}
                        disabled={loading}
                        className={`btn ${status?.isRunning ? 'bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30' : 'btn-primary'} flex items-center gap-2`}
                    >
                        {loading ? (
                            <RefreshCw className="w-5 h-5 animate-spin" />
                        ) : status?.isRunning ? (
                            <><Pause className="w-5 h-5" /> Stop</>
                        ) : (
                            <><Play className="w-5 h-5" /> Start</>
                        )}
                    </button>
                </div>

                {error && (
                    <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm">
                        {error}
                    </div>
                )}

                {/* Stats */}
                <div className="grid grid-cols-4 gap-4 mb-6">
                    <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                        <div className="text-2xl font-bold text-white">{status?.alertsProcessed || 0}</div>
                        <div className="text-xs text-slate-400">Alerts Processed</div>
                    </div>
                    <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                        <div className="text-2xl font-bold text-white">{status?.alertsInQueue || 0}</div>
                        <div className="text-xs text-slate-400">In Queue</div>
                    </div>
                    <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                        <div className="text-2xl font-bold text-white">{status?.activeInvestigations || 0}</div>
                        <div className="text-xs text-slate-400">Active Investigations</div>
                    </div>
                    <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                        <div className={`text-2xl font-bold ${status?.isRunning ? 'text-green-400' : 'text-slate-500'}`}>
                            {status?.isRunning ? '● Active' : '○ Stopped'}
                        </div>
                        <div className="text-xs text-slate-400">Status</div>
                    </div>
                </div>

                {/* Performance Metrics - ROI Story */}
                <div className="grid grid-cols-4 gap-4 p-4 bg-gradient-to-r from-primary-500/10 via-purple-500/10 to-blue-500/10 rounded-xl border border-primary-500/20">
                    <div className="text-center">
                        <div className="text-2xl font-bold text-primary-400">2.3m</div>
                        <div className="text-xs text-slate-400">Avg Response (MTTR)</div>
                    </div>
                    <div className="text-center border-l border-slate-700/50">
                        <div className="text-2xl font-bold text-green-400">78%</div>
                        <div className="text-xs text-slate-400">Auto-Resolved</div>
                    </div>
                    <div className="text-center border-l border-slate-700/50">
                        <div className="text-2xl font-bold text-orange-400">12%</div>
                        <div className="text-xs text-slate-400">Escalated</div>
                    </div>
                    <div className="text-center border-l border-slate-700/50">
                        <div className="text-2xl font-bold text-yellow-400">27%</div>
                        <div className="text-xs text-slate-400">False Positives</div>
                    </div>
                </div>
            </div>

            {/* Recent Alerts */}
            <div className="glass rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-primary-400" />
                        Recent Alerts
                    </h3>
                    <button
                        onClick={fetchAlerts}
                        className="text-slate-400 hover:text-white transition-colors"
                    >
                        <RefreshCw className="w-4 h-4" />
                    </button>
                </div>

                {alerts.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                        <Shield className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No alerts yet. Start monitoring to begin.</p>
                    </div>
                ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                        {alerts.map(({ alert, investigation }) => (
                            <div
                                key={alert.id}
                                className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50 hover:border-slate-600/50 transition-colors"
                            >
                                <div className="flex items-start justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                        <span className={`px-2 py-0.5 rounded text-xs font-medium border ${getSeverityColor(alert.severity)}`}>
                                            {alert.severity.toUpperCase()}
                                        </span>
                                        <span className="text-xs text-slate-500">{alert.source}</span>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        {getStatusIcon(investigation.status)}
                                        <span className="text-xs text-slate-400 capitalize">{investigation.status.replace('_', ' ')}</span>
                                    </div>
                                </div>
                                <h4 className="font-medium text-white mb-1">{alert.title}</h4>
                                <p className="text-sm text-slate-400 line-clamp-2">{alert.description}</p>
                                <div className="flex items-center justify-between mt-3 text-xs text-slate-500">
                                    <span>{new Date(alert.timestamp).toLocaleTimeString()}</span>
                                    <button className="text-primary-400 hover:text-primary-300 flex items-center gap-1">
                                        View Details <ArrowUpRight className="w-3 h-3" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
